import { getBposNavBarData, getBposNavBarDataAsync } from 'owa-bpos-store/lib/bposNavBarData';
import { isFeatureEnabled } from 'owa-feature-flags';
import {
    M365ApplicationConnection,
    M365ApplicationEdge,
    M365HubApplication,
    M365PlatformApplication,
} from 'owa-graph-schema';
import { OfficeApp } from 'owa-left-rail-utils/lib/store/schema/OfficeApp';
import { getNavBarLinkData } from 'owa-left-rail-utils/lib/utils/getNavBarLinkData';
import { getModuleForTasks, ModuleForTasks } from 'owa-todo-utils/lib/utils/moduleAccessUtils';

import { getAcquisitionLink } from './data/getAppStoreAppAcquisitionLink';
import getExternalTilesData from './data/getExternalTilesData';
import getOutlookTilesData from './data/getOutlookTilesData';

import type { AppBarControlQuery } from './graphql/queries/__generated__/AppBarBootstrapData.interface';

type M365Application = M365HubApplication | M365PlatformApplication;

function createEdge(appTileData: M365Application): M365ApplicationEdge {
    return {
        __typename: 'M365ApplicationEdge',
        node: { ...appTileData },
    };
}

export async function getStaticData(): Promise<AppBarControlQuery> {
    const tasksModule = getModuleForTasks();
    const outlookBuiltInApps = [
        'DDEA774C-382B-47D7-AAB5-ADC2139A802B' /* Mail */,
        '355FBD79-3BA2-4554-8F2D-0300FDE91F30' /* People */,
        '8CBEB86F-83E1-43B5-AABA-CD3514322F0B' /* Calendar */,
    ];
    const optionalAppsValidation: { [appId: string]: boolean } = {
        // These are keys to tiles defined in 'data/getOutlookTiles'
        'D7A12BEA-E4FE-49A7-8CD1-C2E6D53722A6' /* Files */: isFeatureEnabled('doc-fileshub'),
        '59391057-d7d7-49fd-a041-d8e4080f05ec' /* To Do */:
            tasksModule === ModuleForTasks.ToDo && !isFeatureEnabled('mos-todoApp'),
        'e9f9b6d1-7948-4b5b-84f2-8a97e3fdb427' /* Tasks */:
            tasksModule === ModuleForTasks.LegacyTasks,
        '9d4ccf05-fca9-4c39-8e31-28f49a4f8e0a' /* Eventify */: isFeatureEnabled(
            'eventify-includeInModuleSwitcher'
        ),
        '261d33d6-15ba-4e1d-b298-8096960fab40' /* Feed */: isFeatureEnabled('feed-app'),
        Test: isFeatureEnabled('mos-testApp'),
    };

    const appTilesToRender: M365ApplicationConnection = {
        __typename: 'M365ApplicationConnection',
        edges: [],
        appAcquisitionLinks: [],
    };

    getOutlookTilesData().forEach(appTile => {
        const shouldRenderApp =
            outlookBuiltInApps.indexOf(appTile.id) > -1 ||
            (appTile.id in optionalAppsValidation && optionalAppsValidation[appTile.id]);
        if (shouldRenderApp) {
            appTilesToRender.edges.push(createEdge(appTile));
        }
    });

    const selectedExternalApps: OfficeApp[] = [
        '9b049ed6-fc8b-40c7-815b-bf84e344d270', // Org Explorer,
        'Word',
        'Excel',
        'Powerpoint',
        'OneNote',
        '4c4ec2e8-4a2c-4bce-8d8f-00fc664a4e5b', // Bookings,
        'db5e5970-212f-477f-a3fc-2227dc7782bf', // Communities,
    ];

    if (tasksModule === ModuleForTasks.ToDo && isFeatureEnabled('mos-todoApp')) {
        selectedExternalApps.push('0d5c91ee-5be2-4b79-81ed-23e6c4580427'); // ToDo app
    }

    // BposNavBarData contains data about user and tenant available product licenses
    if (!getBposNavBarData()) {
        await getBposNavBarDataAsync('getStaticTiles', 'worker', true /* ignore auth error */);
    }
    const bposNavBarData = getBposNavBarData() || undefined;

    selectedExternalApps.forEach(appTile => {
        const navBarLinkData = getNavBarLinkData(appTile, bposNavBarData);
        if (navBarLinkData) {
            const m365App = getExternalTilesData(appTile, navBarLinkData);
            appTilesToRender.edges.push(createEdge(m365App.node));
        }
    });
    appTilesToRender.appAcquisitionLinks!.push(...(await getAcquisitionLink(bposNavBarData)));

    return {
        m365Apps: {
            __typename: 'M365ApplicationConnection',
            ...appTilesToRender,
        },
    };
}
